import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/use-auth";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { SponsoredAd } from "@shared/schema";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MySponsorshipsManager from "@/components/MySponsorshipsManager";
import { Terminal, Gamepad2, Info, Loader2 } from "lucide-react";
import { useLocalStorage } from 'usehooks-ts';
import { useLocation } from 'wouter';

// --- Type Definitions ---
type StartSessionResponse = {
    joinToken: string;
};

type LastLaunchedGame = {
    adKey: string;
    gameName: string;
    gameThumbnailUrl: string;
    launchedAt: string;
    tokensPerPlayerPerHour: number;
};

// --- Constants ---
const NAVBAR_HEIGHT = 64;

// --- Animation Variants ---
const gridContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.08 },
    },
};

const gridItemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
};

// --- Hooks ---
const useLastLaunchedGame = () => {
    return useLocalStorage<LastLaunchedGame | null>('lastLaunchedGame', null);
};

// --- Main Components for "Browse & Play" Tab ---

const AdGrid = ({ onPlayClick, isStartingSession, activeAdKey, lastLaunchedGame, estimatedEarnings }: { onPlayClick: (ad: SponsoredAd) => void, isStartingSession: boolean, activeAdKey: string | null, lastLaunchedGame: LastLaunchedGame | null, estimatedEarnings: number }) => {
    const { data: activeAds, isLoading, error } = useQuery<SponsoredAd[], Error>({
        queryKey: ["active-sponsored-ads"],
        queryFn: () => apiRequest("GET", "/api/sponsored-ads/active").then(res => res.json()),
    });

    if (isLoading) {
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {Array.from({ length: 8 }).map((_, i) => (
                    <Card key={i}><CardHeader className="p-0"><Skeleton className="aspect-video w-full rounded-t-lg" /></CardHeader><CardContent className="p-4 space-y-2"><Skeleton className="h-5 w-3/4" /><Skeleton className="h-4 w-1/2" /></CardContent><CardFooter className="p-4"><Skeleton className="h-10 w-full" /></CardFooter></Card>
                ))}
            </div>
        );
    }

    if (error) {
        return <Alert variant="destructive"><Terminal className="h-4 w-4" /><AlertTitle>Failed to Load Games</AlertTitle><AlertDescription>{error.message || "An unknown error occurred."}</AlertDescription></Alert>;
    }

    if (!activeAds || activeAds.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border p-12 text-center h-96">
                <Gamepad2 className="w-16 h-16 text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold">No Games Available</h3>
                <p className="text-muted-foreground mt-2">There are no sponsored games to play right now. Check back later!</p>
            </div>
        );
    }

    return (
        <motion.div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" variants={gridContainerVariants} initial="hidden" animate="visible">
            {activeAds.map(ad => {
                const isCurrentlyPlaying = lastLaunchedGame?.adKey === ad.adKey;
                return (
                    <motion.div key={ad.adKey} variants={gridItemVariants}>
                        <Card className={`flex flex-col h-full bg-background/50 hover:bg-background/80 transition-all duration-300 hover:scale-105 hover:shadow-lg ${isCurrentlyPlaying ? 'ring-2 ring-primary shadow-primary/20' : ''}`}>
                            <CardHeader className="p-0 relative">
                                <img src={ad.gameThumbnailUrl} alt={ad.gameName} className="aspect-video w-full rounded-t-lg object-cover" />
                                {isCurrentlyPlaying && (
                                    <div className="absolute top-2 right-2 bg-primary text-primary-foreground px-2 py-1 text-xs font-bold rounded-md animate-pulse">
                                        PLAYING
                                    </div>
                                )}
                            </CardHeader>
                            <CardContent className="flex-grow p-4">
                                <h3 className="font-semibold text-lg truncate">{ad.gameName}</h3>
                                {isCurrentlyPlaying ? (
                                    <div className="text-sm text-muted-foreground mt-1">
                                        Estimated earnings: <span className="font-bold text-primary">{estimatedEarnings}</span> tokens
                                    </div>
                                ) : (
                                    <p className="text-sm text-muted-foreground">
                                        Earn up to <span className="font-bold text-primary">{ad.tokensPerPlayerPerHour}</span> tokens per hour
                                    </p>
                                )}
                            </CardContent>
                            <CardFooter className="p-4">
                                <Button className="w-full" onClick={() => onPlayClick(ad)} disabled={isStartingSession}>
                                    {isStartingSession && activeAdKey === ad.adKey ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Gamepad2 className="mr-2 h-4 w-4" />}
                                    {isCurrentlyPlaying ? "Play Again" : "Play Now"}
                                </Button>
                            </CardFooter>
                        </Card>
                    </motion.div>
                )
            })}
        </motion.div>
    );
};

const BrowseAndPlay = () => {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [lastLaunchedGame, setLastLaunchedGame] = useLastLaunchedGame();
    const [activeAdKey, setActiveAdKey] = useState<string | null>(null);
    const [estimatedEarnings, setEstimatedEarnings] = useState(0);
    const [isStatusCheckEnabled, setIsStatusCheckEnabled] = useState(false);
    const isInitialLaunch = useRef(false);

    useEffect(() => {
        if (!lastLaunchedGame) {
            setIsStatusCheckEnabled(false);
            return;
        }

        if (isInitialLaunch.current) {
            const timer = setTimeout(() => {
                setIsStatusCheckEnabled(true);
                isInitialLaunch.current = false;
            }, 600000); // 10 minutes

            return () => clearTimeout(timer);
        } else {
            setIsStatusCheckEnabled(true);
        }
    }, [lastLaunchedGame]);

    const { data: sessionStatus } = useQuery<{ isActive: boolean }>({
      queryKey: ['sessionStatus', lastLaunchedGame?.adKey],
      queryFn: async () => {
        if (!lastLaunchedGame) return { isActive: false };
        const res = await apiRequest("GET", `/api/sponsored-ads/session/status/${lastLaunchedGame.adKey}`);
        return res.json();
      },
      enabled: isStatusCheckEnabled,
      refetchInterval: 600000,
      refetchOnWindowFocus: true,
      retry: false,
    });

    useEffect(() => {
      if (isStatusCheckEnabled && sessionStatus && !sessionStatus.isActive && lastLaunchedGame) {
        toast({
            title: "Session Ended",
            description: `Your earnings from ${lastLaunchedGame.gameName} have been credited.`,
        });
        queryClient.invalidateQueries({ queryKey: ["me"] });
        queryClient.invalidateQueries({ queryKey: ["transactionHistory"] });
        setLastLaunchedGame(null);
      }
    }, [sessionStatus, isStatusCheckEnabled, lastLaunchedGame, setLastLaunchedGame, toast, queryClient]);

    // This effect handles the earnings counter.
    useEffect(() => {
        if (!lastLaunchedGame) {
            setEstimatedEarnings(0);
            return;
        }

        if (new Date().getTime() - new Date(lastLaunchedGame.launchedAt).getTime() > 3600 * 1000 * 2) {
            setLastLaunchedGame(null);
            return;
        }

        const tokensPerSecond = lastLaunchedGame.tokensPerPlayerPerHour / 3600;

        const interval = setInterval(() => {
            const now = new Date().getTime();
            const start = new Date(lastLaunchedGame.launchedAt).getTime();
            const elapsedSeconds = (now - start) / 1000;
            setEstimatedEarnings(Math.floor(elapsedSeconds * tokensPerSecond));
        }, 1000);

        return () => clearInterval(interval);
    }, [lastLaunchedGame, setLastLaunchedGame]);

    const startSessionMutation = useMutation<StartSessionResponse, Error, SponsoredAd>({
      mutationFn: (ad) => {
        setActiveAdKey(ad.adKey);
        return apiRequest("POST", "/api/sponsored-ads/session/start", { adKey: ad.adKey }).then(res => res.json());
      },
      onSuccess: (data, ad) => {
        if (!data.joinToken) {
          toast({ title: "Error", description: "Could not retrieve a join token for the game.", variant: "destructive" });
          return;
        }

        isInitialLaunch.current = true;
  
        const launchUrl = `roblox://experiences/start?placeId=${ad.placeId}&launchData=${encodeURIComponent(data.joinToken)}`;
  
        setLastLaunchedGame({
          adKey: ad.adKey,
          gameName: ad.gameName,
          gameThumbnailUrl: ad.gameThumbnailUrl,
          launchedAt: new Date().toISOString(),
          tokensPerPlayerPerHour: ad.tokensPerPlayerPerHour
        });
  
        toast({
          title: "Game Launched!",
          description: "Your rewards will be credited automatically after you leave the game.",
        });
  
        window.location.href = launchUrl;
      },
      onError: (error: any) => {
        const message = error.response?.data?.message || "An unknown error occurred while starting the session.";
        toast({ title: "Launch Failed", description: message, variant: "destructive" });
      },
      onSettled: () => {
        setActiveAdKey(null);
      }
    });
  
    return (
      <>
        <AdGrid
          onPlayClick={(ad) => startSessionMutation.mutate(ad)}
          isStartingSession={startSessionMutation.isPending}
          activeAdKey={activeAdKey}
          lastLaunchedGame={lastLaunchedGame}
          estimatedEarnings={estimatedEarnings}
        />
      </>
    )
  }  

const SponsoredGamesPage = () => {
    const { user } = useAuth();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [location, setLocation] = useLocation();
    const [activeTab, setActiveTab] = useState("browse");
    const [isCreateOpen, setCreateOpen] = useState(false);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.has('purchase_success')) {
            toast({
                title: "Purchase Successful!",
                description: "Your token balance has been updated.",
            });
            queryClient.invalidateQueries({ queryKey: ['me'] });
            setActiveTab("manage");
            setCreateOpen(true);

            // Clean up the URL using wouter's setLocation
            setLocation(location.split('?')[0], { replace: true });
        }
    }, [toast, queryClient, location, setLocation]);

    if (!user) {
        return (
            <div className="p-4 md:p-8">
                <Alert variant="destructive">
                    <Terminal className="h-4 w-4" />
                    <AlertTitle>Authentication Required</AlertTitle>
                    <AlertDescription>You must be logged in to view sponsored games.</AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8" style={{ minHeight: `calc(100vh - ${NAVBAR_HEIGHT}px)` }}>
            <div className="mb-6">
                <h2 className="text-3xl font-bold tracking-tight">Sponsored Games</h2>
                <p className="text-muted-foreground mt-1">Earn tokens by playing games or create your own sponsorship to attract players.</p>
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger value="browse">Browse & Play</TabsTrigger>
                    <TabsTrigger value="manage">Manage My Sponsorships</TabsTrigger>
                </TabsList>
                <TabsContent value="browse">
                    <BrowseAndPlay />
                </TabsContent>
                <TabsContent value="manage">
                    <MySponsorshipsManager isCreateOpen={isCreateOpen} setCreateOpen={setCreateOpen} />
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default SponsoredGamesPage;
